import React, { useEffect, useState, useCallback, useRef } from 'react';
import { GoogleMap, useLoadScript, Marker } from '@react-google-maps/api';

const libraries = ['places'];
const mapContainerStyle = {
  width: '100vw',
  height: '100vh',
};

const GoogleMapComponent = ({ latitude, longitude, onSelectLocation }) => {
  const [selectedPlace, setSelectedPlace] = useState({
    lat: latitude || 41.3113642,
    lng: longitude || 69.28267819999999,
  });
  const markerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (latitude && longitude) {
      const newLocation = { lat: latitude, lng: longitude };
      setSelectedPlace(newLocation);
      if (mapRef.current) {
        mapRef.current.panTo(newLocation);
      }
      animateMarker();
    }
  }, [latitude, longitude]);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_KEY,
    libraries,
  });

  const onMapClick = useCallback((event) => {
    const newMarker = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng(),
    };

    setSelectedPlace(newMarker);
    if (mapRef.current) {
      mapRef.current.panTo(newMarker);
    }
    animateMarker();
    reverseGeocode(newMarker);
  }, []);

  const onMarkerDragEnd = useCallback((event) => {
    const newPosition = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng(),
    };

    setSelectedPlace(newPosition);
    if (mapRef.current) {
      mapRef.current.panTo(newPosition);
    }
    animateMarker();
    reverseGeocode(newPosition);
  }, []);

  const animateMarker = () => {
    if (markerRef.current) {
      markerRef.current.setAnimation(window.google.maps.Animation.BOUNCE);
      setTimeout(() => {
        markerRef.current.setAnimation(null);
      }, 200); // Bounce animation duration
    }
  };

  const extractLocationDetails = (result, exactCoordinates) => {
    const addressComponents = result.address_components;
    const locationDetails = {
      location: result.formatted_address,
      state: getAddressComponent(addressComponents, 'administrative_area_level_1'),
      city: getAddressComponent(addressComponents, 'locality'),
      country: getAddressComponent(addressComponents, 'country'),
      latitude: exactCoordinates.lat,
      longitude: exactCoordinates.lng,
    };
    return locationDetails;
  };

  const reverseGeocode = async (marker) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${marker.lat},${marker.lng}&key=${process.env.REACT_APP_GOOGLE_MAP_KEY}`
      );
      const data = await response.json();
      const results = data.results;
      const exactCoordinates = { lat: marker.lat, lng: marker.lng };
      
      let locationDetails = {
        location: 'Unknown location',
        state: '',
        city: '',
        country: '',
        latitude: exactCoordinates.lat,
        longitude: exactCoordinates.lng,
      };
      
      if (results.length > 0) {
        locationDetails = extractLocationDetails(results[0], exactCoordinates);
        // Check if plus_code is available and store the compound code in location
        if (data.plus_code && data.plus_code.compound_code) {
          locationDetails.location = data.plus_code.compound_code;
        }
      }
      
      onSelectLocation(locationDetails);
    } catch (error) {
      console.error('Geocode failed due to: ' + error);
      onSelectLocation({
        location: 'Unknown location',
        state: '',
        city: '',
        country: '',
        latitude: marker.lat,
        longitude: marker.lng,
      });
    }
  };

  const getAddressComponent = (addressComponents, type) => {
    const component = addressComponents.find(component => component.types.includes(type));
    return component ? component.long_name : '';
  };

  if (loadError) {
    return <div>Error loading maps</div>;
  }

  if (!isLoaded) {
    return <div>Loading maps</div>;
  }

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      zoom={10}
      center={selectedPlace}
      onClick={onMapClick}
      mapContainerClassName="w-100 h-400px shadow-sm rounded-4"
      onLoad={map => (mapRef.current = map)}
    >
      <Marker
        position={selectedPlace}
        draggable={true}
        onDragEnd={onMarkerDragEnd}
        onLoad={marker => (markerRef.current = marker)}
      />
    </GoogleMap>
  );
};

export default GoogleMapComponent;
